import { formatInTimeZone } from 'date-fns-tz';

import {
    formatTimeRange,
    groupSegmentsByDay,
    type ScheduleDayGroup,
    type ShiftScheduleSegment,
} from '@apps/web/components/caregivers/caregiver_availability_schedule_model';
import { toDateKey } from '@apps/web/lib/date_utils';
import { type Shift } from '@libraries/typescript/api_client';

const EXCLUDED_SCHEDULE_STATUSES = new Set<Shift['status']>([
    'cancelled',
    'missing',
    'not_scheduled',
]);
const OPEN_SCHEDULE_STATUSES = new Set<Shift['status']>(['open']);

/**
 * Maps a client's shifts onto the shared schedule day-group model used by
 * the availability month grid. `details` carries the covering caregiver's
 * full name; unfilled shifts render as "Open".
 */
export function buildClientScheduleDayGroups({
    shifts,
    timezone,
    formatName = (name: string | null | undefined) => name ?? '',
}: {
    readonly shifts: ReadonlyArray<Shift>;
    readonly timezone: string;
    readonly formatName?: (fullName: string | null | undefined) => string;
}): Array<ScheduleDayGroup<ShiftScheduleSegment>> {
    const segments = shifts
        .filter((shift) => !EXCLUDED_SCHEDULE_STATUSES.has(shift.status))
        .map((shift): ShiftScheduleSegment => {
            const start = new Date(shift.shift_start_time);
            const open = OPEN_SCHEDULE_STATUSES.has(shift.status);
            // `name` is the stored full name and what the rest of the app
            // shows; the split fields are optional, so they only cover a
            // record whose full name is blank.
            const caregiver = shift.caregiver;
            const caregiverName = formatName(
                caregiver?.name?.trim() ||
                    [caregiver?.first_name, caregiver?.last_name]
                        .filter(Boolean)
                        .join(' ')
                        .trim(),
            );
            return {
                kind: 'shift',
                id: shift.id,
                parentId: shift.id,
                dayKey: toDateKey(start, timezone),
                dayLabel: formatInTimeZone(start, timezone, 'EEE, MMM d'),
                startTime: shift.shift_start_time,
                endTime: shift.shift_end_time,
                timeLabel: formatTimeRange(
                    shift.shift_start_time,
                    shift.shift_end_time,
                    timezone,
                ),
                sortTimeMs: start.getTime(),
                details: open ? 'Open' : caregiverName || 'Assigned',
                open,
            };
        });
    return groupSegmentsByDay(segments);
}

export function countClientScheduleFill(
    groups: ReadonlyArray<ScheduleDayGroup<ShiftScheduleSegment>>,
): { readonly filled: number; readonly open: number } {
    let filled = 0;
    let open = 0;
    for (const group of groups) {
        for (const segment of group.items) {
            if (segment.open) {
                open += 1;
            } else {
                filled += 1;
            }
        }
    }
    return { filled, open };
}
